import React from 'react';
import { Alert, Button, Space, Spin, Typography } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { VirtualCollection } from 'flare-ui-primitives';
import { resolveSkillIcon } from '../capabilities/capabilityIcons.jsx';

const { Text } = Typography;
const text = (value) => String(value ?? '').trim();
const invoke = (action, ...args) => Promise.resolve(action?.(...args)).catch(() => {});
const conditionalValue = (condition, whenTrue) => (condition ? whenTrue : null);

function ArtifactRows({ collection, projectId, scrollElement }) {
  const items = Array.isArray(collection.items) ? collection.items : [];
  return (
    <VirtualCollection
      ariaLabel="产物列表"
      className="flare-project-collection-rows"
      continuation={{
        error: collection.error ?? null,
        hasMore: collection.hasMore === true,
        key: text(collection.window?.nextCursor),
        loading: collection.status === 'loading_more',
      }}
      getItemKey={artifact => text(artifact.artifact_id)}
      items={items}
      layout={{ columns: 1, endThreshold: 96, estimateSize: 44, gap: 6, overscan: 4 }}
      onLoadMore={() => invoke(collection.onLoadMore)}
      renderItem={(artifact) => {
        const artifactId = text(artifact.artifact_id);
        const label = [text(Object(artifact.skillPresentation).label), text(artifact.title), text(artifact.artifact_type), artifactId].find(Boolean);
        const selected = artifactId === text(collection.selectedArtifactId);
        const loading = artifactId === text(collection.loadingArtifactId);
        return (
          <button
            aria-busy={loading || undefined}
            aria-pressed={selected}
            className={['flare-project-collection-row', selected ? 'is-active' : ''].filter(Boolean).join(' ')}
            data-artifact-id={artifactId}
            disabled={loading}
            onClick={() => invoke(collection.onSelectArtifact, artifact)}
            title={text(artifact.title) || undefined}
            type="button"
          >
            <span className="flare-project-collection-row-icon">
              {artifact.skillPresentation ? resolveSkillIcon(artifact.skillPresentation) : <FileTextOutlined />}
            </span>
            <span className="flare-project-collection-row-title">
              {label}
            </span>
            {loading ? <Spin size="small" /> : null}
          </button>
        );
      }}
      scopeKey={`project-artifacts:${projectId}`}
      scrollElement={scrollElement}
    />
  );
}

function RetryButton({ collection }) {
  return <Button onClick={() => invoke(collection.onRetry)} size="small" type="link">重试</Button>;
}

function LoadingState() {
  return <div className="flare-project-collection-state"><Spin size="small" /></div>;
}

function ErrorState({ collection }) {
  return (
    <div className="flare-project-collection-state">
      <Text type="danger">加载产物失败</Text>
      <RetryButton collection={collection} />
    </div>
  );
}

function SelectionError({ error }) {
  if (!error) return null;
  return <Text type="danger">{text(error.message || error)}</Text>;
}

function CollectionBody({ collection, projectId, scrollElement, state, themeTokens }) {
  const hasItems = Array.isArray(collection.items) && collection.items.length > 0;
  const partial = state === 'partial';
  const renderItems = ['ready', 'partial', 'loading_more'].includes(state) && hasItems;
  return (
    <Space orientation="vertical" size={10} style={{ width: '100%' }}>
      {conditionalValue(state === 'empty', (
        <div className="flare-project-collection-state">
          <FileTextOutlined style={{ color: themeTokens.textMuted, fontSize: 24 }} />
          <Text type="secondary">暂无产物</Text>
        </div>
      ))}
      {conditionalValue(partial, <Alert message="部分产物加载失败" showIcon type="warning" />)}
      {conditionalValue(renderItems, (
        <ArtifactRows collection={collection} projectId={projectId} scrollElement={scrollElement} />
      ))}
      {conditionalValue(partial, <RetryButton collection={collection} />)}
      <SelectionError error={collection.selectionError} />
    </Space>
  );
}

const STATE_COMPONENTS = Object.freeze({ loading: LoadingState, error: ErrorState });

export default function ProjectArtifactCollection({ artifactCollection, projectId, scrollElement, themeTokens }) {
  const collection = artifactCollection && typeof artifactCollection === 'object'
    ? artifactCollection
    : {};
  const state = text(collection.status) || 'empty';
  const StateComponent = STATE_COMPONENTS[state];
  return StateComponent
    ? <StateComponent collection={collection} />
    : <CollectionBody collection={collection} projectId={projectId} scrollElement={scrollElement} state={state} themeTokens={themeTokens} />;
}
